import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent'
import { PieChart, Pie, Cell, Tooltip } from 'recharts'
import { TooltipContentProps } from 'recharts/types/component/Tooltip'
import * as React from 'react'
import pluralize from 'pluralize'
import { CheckinStatusLabel } from 'components/checkin-status-label'
import { RatingScaleLabel } from 'store/modules/checkins'

export type SkillRatioDonutChartCounts = Record<string, number> & {
  not_checked_in: number
}

export type SkillRatioDonutChartProps = {
  counts: SkillRatioDonutChartCounts
  emptyState?: React.ReactNode
  hideTitle?: boolean
  labels?: RatingScaleLabel[]
  loading?: boolean
}

type ChartEntry = {
  name: string
  value: number
  total: number
  color: string
  statusColor?: string
  statusLabel?: string
}

export const SkillRatioDonutChart: React.VFC<SkillRatioDonutChartProps> = (
  props,
) => {
  const { counts, emptyState, hideTitle, labels, loading } = props

  const data = React.useMemo<ChartEntry[]>(() => {
    const sorted = labels
      ? [...labels].sort((a, b) => a.scaleValue - b.scaleValue)
      : []

    const entries: ChartEntry[] = sorted.map((label) => ({
      name: label.labelName,
      value: counts[label.labelSlug] ?? 0,
      total: 0, // filled below
      color: label.color,
      statusColor: label.color,
      statusLabel: label.labelName,
    }))

    if (counts.not_checked_in > 0) {
      entries.push({
        name: 'not_checked_in',
        value: counts.not_checked_in,
        total: 0,
        color: '#E5E7EB',
        statusLabel: 'Not Checked-In',
      })
    }

    const total = entries.reduce((sum, e) => sum + e.value, 0)
    entries.forEach((e) => (e.total = total))

    return total > 0
      ? entries
      : [{ name: 'no_checkins', value: 1, total: 1, color: '#E5E7EB' }]
  }, [counts, labels])

  const total = data.reduce((sum, e) => sum + e.value, 0)

  return (
    <div
      className={`flex flex-col items-center${loading ? ' animate-pulse' : ''}`}
    >
      {!hideTitle && (
        <>
          <h3 className="font-bold text-gray-900 text-xl">Skill ratio</h3>
          <p className="mb-5 text-gray-600 text-xs">
            Ratio of levels for all skills
          </p>
        </>
      )}
      <div className="relative">
        <PieChart
          className="flex !cursor-pointer"
          height={300}
          margin={{ bottom: 0, top: 0, left: 0, right: 0 }}
          width={300}
        >
          <Pie
            cx="50%"
            cy="50%"
            data={data}
            dataKey="value"
            innerRadius={130}
            outerRadius={150}
            stroke={total > 0 ? '#FFFFFF' : ''}
          >
            {data.map((entry) => (
              <Cell key={`cell-${entry.name}`} fill={entry.color} />
            ))}
          </Pie>
          {total > 0 && (
            <Tooltip
              content={<TooltipContent />}
              cursor={{ fill: 'transparent' }}
            />
          )}
        </PieChart>
        {total === 0 && !loading && emptyState}
      </div>
    </div>
  )
}

const TooltipContent = (
  props: Partial<TooltipContentProps<ValueType, NameType>>,
) => {
  const { payload } = props
  const chartData = payload?.[0]
  if (!chartData) return null

  const { value, total, statusColor, statusLabel, name } =
    chartData.payload as ChartEntry
  const percentage = Math.round((value / total) * 100)

  return (
    <div className="bg-white border border-gray-100 flex gap-2 items-center min-w-[220px] p-3 rounded text-xs w-auto whitespace-nowrap">
      <CheckinStatusLabel
        status={name}
        statusColor={statusColor}
        statusLabel={statusLabel}
      />
      <p className="mb-0 text-gray-600">
        {value} {pluralize('skill', value)} ({percentage}%)
      </p>
    </div>
  )
}
